import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { dmsService } from '../../services/dmsService';

export const UserDocumentList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Use the passed user object from router state, or fallback if none
  const user = location.state?.user;
  
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user && !id) return;
    
    const fetchUserDocuments = async () => {
      setLoading(true);
      try {
        // Fetch documents uploaded by this user using the backend uploader filter
        const uploaderId = user?.id || id;
        const data = await dmsService.getDocuments({ uploader: String(uploaderId) });
        setDocuments(data);
      } catch (err) {
        console.error('Failed to fetch user documents', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDocuments();
  }, [user, id]);

  if (!user && !id) {
    return (
      <div className="page-container">
        <p>No user data found. Please navigate from the User Management page.</p>
        <button className="primary-btn" onClick={() => navigate('/admin/users')}>Go Back</button>
      </div>
    );
  }

  // Fallback user display details if state is not passed
  const displayUser = user || {
    first_name: 'User',
    last_name: `#${id}`,
    user_name: 'N/A',
    role: 'N/A',
    department: 'N/A'
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button 
              onClick={() => navigate(-1)} 
              style={{ 
                color: 'var(--text-dark)', 
                background: 'transparent', 
                border: 'none', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                padding: 0 
              }} 
              title="Back"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>Uploaded Documents for User</h1>
          </div>
        </div>
      </div>

      <div className="vendor-info-card" style={{ display: 'block', marginBottom: '2rem', padding: '1.5rem 2rem', background: 'white', borderRadius: '8px', border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '1.1rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>User Details</h3>
        <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Name</p>
            <p className="info-value" style={{ fontWeight: 600 }}>{displayUser.first_name} {displayUser.last_name}</p>
          </div>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Username</p>
            <p className="info-value">{displayUser.user_name}</p>
          </div>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Role</p>
            <p className="info-value" style={{ textTransform: 'capitalize' }}>{displayUser.role}</p>
          </div>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Department</p>
            <p className="info-value" style={{ color: 'var(--primary)', fontWeight: 600 }}>{displayUser.department || 'Unassigned'}</p>
          </div>
        </div>
      </div>

      <h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>Uploaded Documents</h3>
      <div className="list-container">
        {loading ? (
          <p style={{ padding: '1rem', color: 'var(--text-light)' }}>Loading documents...</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Document Name</th>
                <th>Category</th>
                <th>Upload Date</th>
                <th>Active Version</th>
                <th>Confidentiality</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => (
                <tr key={doc.id}>
                  <td><strong>{idx + 1}</strong></td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600 }}>{doc.title}</span>
                      {doc.document_type_detail && (
                        <div style={{ marginTop: '0.15rem' }}>
                          <span
                            style={{
                              fontSize: '0.75rem',
                              backgroundColor: 'rgba(71, 49, 146, 0.08)',
                              color: 'var(--primary)',
                              padding: '0.05rem 0.35rem',
                              borderRadius: '4px',
                              display: 'inline-block',
                              fontWeight: 500,
                            }}
                          >
                            {doc.document_type_detail.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{doc.category_detail?.name || 'N/A'}</td>
                  <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                  <td>
                    {doc.current_version_detail ? (
                      <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                        v{doc.current_version_detail.version_number}
                      </span>
                    ) : (
                      <span className="text-muted">No file</span>
                    )}
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor:
                          doc.confidentiality === 'PUBLIC'
                            ? '#d1fae5'
                            : doc.confidentiality === 'INTERNAL'
                            ? '#e0f2fe'
                            : doc.confidentiality === 'CONFIDENTIAL'
                            ? '#fef3c7'
                            : '#fee2e2',
                        color:
                          doc.confidentiality === 'PUBLIC'
                            ? '#065f46'
                            : doc.confidentiality === 'INTERNAL'
                            ? '#0369a1'
                            : doc.confidentiality === 'CONFIDENTIAL'
                            ? '#b45309'
                            : '#b91c1c',
                      }}
                    >
                      {doc.confidentiality}
                    </span>
                  </td>
                </tr>
              ))}
              {documents.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                    No uploaded documents found for this user.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
